import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import StatsCard from '@/components/dashboard/StatsCard';
import { 
  Users,
  Heart,
  DollarSign,
  CheckCircle2,
  Download,
  ArrowRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

export default function EmployerDashboard() {
  const [employer, setEmployer] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [products, setProducts] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      const user = await api.auth.me();

      const systemAdmin = user?.user_role === 'admin' || user?.role === 'ADMIN';
      setIsSystemAdmin(systemAdmin);

      if (systemAdmin) {
        const employersData = await api.entities.Employer.list();
        setEmployers(employersData);
        const defaultEmployerId = employersData?.[0]?.id || '';
        setSelectedEmployerId(defaultEmployerId);
        if (defaultEmployerId) {
          await loadData(defaultEmployerId, true);
        } else {
          setEmployer(null);
          setEmployees([]);
          setEnrollments([]);
          setProducts([]);
        }
        return;
      }
      
      if (user.employer_id) {
        await loadData(user.employer_id, false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (employerId, systemAdmin) => {
    const [employerData, employeesData, enrollmentsData, productsData] = await Promise.all([
      api.entities.Employer.filter({ id: employerId }),
      api.entities.Employee.filter({ employer_id: employerId, is_active: true }),
      api.entities.Enrollment.filter({ employer_id: employerId }),
      systemAdmin ? api.entities.BenefitProduct.filter({ employer_id: employerId }) : api.entities.BenefitProduct.list()
    ]);

    if (employerData.length > 0) setEmployer(employerData[0]);
    setEmployees(employeesData);
    setEnrollments(enrollmentsData);
    setProducts(productsData);
  };

  const handleEmployerChange = async (value) => {
    setSelectedEmployerId(value);
    setLoading(true);
    try {
      await loadData(value, true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const pendingEnrollments = enrollments.filter(e => e.status === 'pending');
  const totalPremiums = activeEnrollments.reduce((sum, e) => sum + (e.premium_amount || 0), 0);
  const enrolledEmployees = new Set(activeEnrollments.map(e => e.employee_id)).size;

  const enrollmentRate = employees.length > 0 
    ? ((enrolledEmployees / employees.length) * 100).toFixed(0) 
    : 0;

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Unknown';
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown';
  };

  // Chart data
  const statusData = [
    { name: 'Enrolled', value: enrolledEmployees, color: '#10b981' },
    { name: 'Not Enrolled', value: Math.max(0, employees.length - enrolledEmployees), color: '#e2e8f0' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            {employer?.company_name || 'Employer'} Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Overview of employee benefits enrollment
          </p>
        </div>
        <Link to={createPageUrl('ExportCensus')}>
          <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
            <Download className="w-4 h-4 mr-2" />
            Export Census
          </Button>
        </Link>
      </div>

      {isSystemAdmin && (
        <Card>
          <CardContent className="p-4">
            <div className="grid gap-2">
              <Label>Employer</Label>
              <Select value={selectedEmployerId} onValueChange={handleEmployerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer" />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatsCard
            title="Total Employees"
            value={employees.length}
            subtitle="Active employees"
            icon={Users}
            gradient="from-blue-500 to-indigo-500"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StatsCard
            title="Enrolled Employees"
            value={enrolledEmployees}
            subtitle={`${enrollmentRate}% enrollment rate`}
            icon={Heart}
            gradient="from-emerald-500 to-teal-500"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StatsCard
            title="Active Enrollments"
            value={activeEnrollments.length}
            subtitle={`${pendingEnrollments.length} pending`}
            icon={CheckCircle2}
            gradient="from-purple-500 to-pink-500"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <StatsCard
            title="Monthly Premiums"
            value={`$${totalPremiums.toLocaleString()}`}
            subtitle="Total deductions"
            icon={DollarSign}
            gradient="from-amber-500 to-orange-500"
          />
        </motion.div>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Enrollment Status Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Heart className="w-5 h-5 text-emerald-500" />
                Enrollment Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-6 mt-4">
                {statusData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-slate-600">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Enrollments */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="lg:col-span-2"
        >
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Enrollments</CardTitle>
              <Link to={createPageUrl('EmployerEnrollments')}>
                <Button variant="ghost" size="sm">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {activeEnrollments.length === 0 ? (
                <div className="text-center py-8">
                  <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No enrollments yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activeEnrollments.slice(0, 5).map((enrollment) => (
                    <div 
                      key={enrollment.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-medium">
                          {getEmployeeName(enrollment.employee_id).charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {getEmployeeName(enrollment.employee_id)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {getProductName(enrollment.product_id)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">${enrollment.premium_amount?.toFixed(2)}/mo</p>
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Link to={createPageUrl('EmployerEmployees')} className="block">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">View Employees</p>
                <p className="text-sm text-slate-500">Manage employee profiles</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to={createPageUrl('EmployerEnrollments')} className="block">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Heart className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">View Enrollments</p>
                <p className="text-sm text-slate-500">Track benefit enrollments</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to={createPageUrl('ExportCensus')} className="block">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Download className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Export Census</p>
                <p className="text-sm text-slate-500">Download employee data</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}